'use client';

import { AlertCircle, Loader2 } from 'lucide-react';
import { useState } from 'react';

import { BaseModal } from '@/components/ui/BaseModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useInviteMember } from '@/lib/query/useInviteMember';
import { usePricing } from '@/lib/query/usePricing';

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function InviteMemberModal({ isOpen, onClose }: InviteMemberModalProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member'>('member');
  const [error, setError] = useState<string | null>(null);

  const inviteMutation = useInviteMember();
  const { data: pricing } = usePricing();

  // Calculate the new price after adding a member
  const newTotalCents =
    pricing && pricing.totalCents !== null
      ? pricing.totalCents + pricing.pricePerMemberCents
      : null;

  const formatPrice = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: pricing?.currency || 'USD',
    }).format(cents / 100);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      await inviteMutation.mutateAsync({ email: email.trim(), role });
      handleClose();
    } catch (err) {
      const error = err as { status?: number; message?: string };
      if (error.status === 409) {
        setError('This user has already been invited or is already a member.');
      } else {
        setError(
          error.message || 'Failed to send invitation. Please try again.'
        );
      }
    }
  };

  const handleClose = () => {
    setEmail('');
    setRole('member');
    setError(null);
    inviteMutation.reset();
    onClose();
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Invite Member"
      size="sm"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email address</Label>
          <Input
            id="email"
            type="email"
            placeholder="colleague@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={inviteMutation.isPending}
            aria-describedby={error ? 'invite-error' : undefined}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="role">Role</Label>
          <Select
            value={role}
            onValueChange={(value: 'admin' | 'member') => setRole(value)}
            disabled={inviteMutation.isPending}
          >
            <SelectTrigger id="role" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="member">Member</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-muted-foreground text-sm">
            Admins can manage billing, members, and settings.
          </p>
        </div>

        {pricing && newTotalCents !== null && (
          <div className="bg-muted/50 rounded-md p-3 text-sm">
            <p className="text-muted-foreground">
              When this member accepts the invitation, your subscription will
              increase to{' '}
              <span className="text-foreground font-medium">
                {formatPrice(newTotalCents)}/month
              </span>
            </p>
          </div>
        )}

        {error && (
          <div
            id="invite-error"
            className="bg-destructive/10 text-destructive flex items-start gap-2 rounded-md p-3 text-sm"
            role="alert"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={inviteMutation.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={inviteMutation.isPending}>
            {inviteMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              'Send Invitation'
            )}
          </Button>
        </div>
      </form>
    </BaseModal>
  );
}
